import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, CloseButton } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';

import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import Actions from 'components/widgets/Notes/Actions';
import Content from 'components/widgets/Notes/Content';
import { useProjectNotes } from 'hooks/useProjectNotes';
import { useToast } from 'hooks/useToast';
import {
  ProjectNotesClickActions,
  ProjectNotesClickActionTypes,
} from 'utils/analytics/constants';
import { formatNotesDate } from 'utils/date';

interface EditableNoteProps {
  targetId: number | string;
  note: string;
  updated_at: string;
  first_name?: string;
  last_name?: string;
  email: string;
  onNoteChanged: () => void;
  trackingData?: {
    sectionName: string;
    subSectionName?: string;
    onTrack: (params: {
      projectId: number | string;
      clickAction: ProjectNotesClickActionTypes;
      clickText: string;
      sectionName: string;
      subSectionName?: string;
    }) => void;
  };
}

const parentCol = {
  width: '100%',
  paddingBottom: '12px',
};
const nameAndTimeRow = { paddingBottom: '4px', gap: 'padding_8' };
const deleteParent = {
  background: Colors.SystemColorNegative,
  borderRadius: '4px',
  color: 'white',
  padding: 'padding_8',
};
const noteDeleted = { gap: 'padding_4' };
const undoBtnContainer = {
  '> button:hover': {
    backgroundColor: 'transparent',
    color: 'none',
  },
};

const EditableNote = ({
  targetId,
  note,
  updated_at,
  first_name,
  last_name,
  email,
  onNoteChanged,
  trackingData,
}: EditableNoteProps) => {
  const [currentNote, setCurrentNote] = useState<string>('');
  const [showButtons, setShowButtons] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isOverLimit, setIsOverLimit] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { createProjectNote, updateProjectNote, deleteProjectNote } = useProjectNotes();
  const { renderCustomToast: toast } = useToast();

  useEffect(() => {
    if (note) {
      setCurrentNote(note);
    }
  }, [note]);

  const userName = useMemo(() => {
    if (first_name && last_name) {
      return `${first_name} ${last_name}`;
    }
    if (first_name) {
      return `${first_name}`;
    }

    return email;
  }, [first_name, last_name, email]);

  const trackNoteAction = useCallback(
    (clickAction: ProjectNotesClickActionTypes, clickText: string) => {
      if (!trackingData) {
        return;
      }

      trackingData.onTrack({
        projectId: targetId,
        clickAction,
        clickText,
        sectionName: trackingData.sectionName,
        subSectionName: trackingData.subSectionName,
      });
    },
    [targetId, trackingData],
  );

  const handleSave = useCallback(() => {
    if (isOverLimit) {
      textareaRef.current?.focus();

      return;
    }
    // Check if an existing note is present, if so update it, else create a new one
    if (note) {
      updateProjectNote.mutate(
        {
          targetId,
          payload: { note: currentNote },
        },
        {
          onSuccess: () => {
            setShowButtons(false);
            setIsEditing(false);
            onNoteChanged();
            trackNoteAction(ProjectNotesClickActions.UPDATE, 'Save');
          },
          onError: () => {
            toast({
              title: 'Failed to save note',
              status: 'error',
              description: 'Unable to save your note. Please try again.',
            });
          },
        },
      );
    } else {
      createProjectNote.mutate(
        {
          ...(typeof targetId === 'number'
            ? { project_id: targetId }
            : { document_id: targetId }),
          note: currentNote,
        },
        {
          onSuccess: () => {
            setShowButtons(false);
            setIsEditing(false);
            onNoteChanged();
            trackNoteAction(ProjectNotesClickActions.CREATE, 'Save');
          },
          onError: () => {
            toast({
              title: 'Failed to save note',
              status: 'error',
              description: 'Unable to save your note. Please try again.',
            });
          },
        },
      );
    }
  }, [
    onNoteChanged,
    currentNote,
    targetId,
    note,
    updateProjectNote,
    createProjectNote,
    isOverLimit,
    textareaRef,
    setShowButtons,
    setIsEditing,
    toast,
    trackNoteAction,
  ]);

  const handleCancel = useCallback(() => {
    setCurrentNote(String(note) || ''); // Reset to the original note
    setShowButtons(false);
    setIsEditing(false);
    trackNoteAction(ProjectNotesClickActions.CANCEL_EDIT, 'Cancel');
  }, [setCurrentNote, setShowButtons, setIsEditing, note, trackNoteAction]);

  const handleEdit = useCallback(() => {
    setShowButtons(true);
    setIsEditing(true);
    textareaRef.current?.focus();
    trackNoteAction(ProjectNotesClickActions.START_EDIT, 'Edit');
  }, [setShowButtons, setIsEditing, textareaRef, trackNoteAction]);

  const undo = useCallback(
    (deletedNote: string, onClose: () => void) => {
      // If the user clicks "Undo", recreate the note
      createProjectNote.mutate(
        {
          ...(typeof targetId === 'number'
            ? { project_id: targetId }
            : { document_id: targetId }),
          note: deletedNote,
        },
        {
          onSuccess: () => {
            onClose();
            onNoteChanged();
            trackNoteAction(ProjectNotesClickActions.UNDO_DELETE, 'Undo');
          },
        },
      );
    },
    [targetId, createProjectNote, onNoteChanged, trackNoteAction],
  );

  const handleDelete = useCallback(
    (deletedNote: string) => {
      // Make the API call to delete the note
      deleteProjectNote.mutate(targetId, {
        onSuccess: () => {
          setCurrentNote('');
          onNoteChanged();
          trackNoteAction(ProjectNotesClickActions.DELETE, 'Delete');
        },
      });

      toast({
        title: 'Note deleted',
        status: 'error',
        duration: 20000, // 20 seconds
        isClosable: true,
        render: ({ onClose }) => (
          <FlexRow sx={deleteParent}>
            <CloseButton onClick={onClose} />
            <Box>
              <FlexRow sx={noteDeleted}>
                <Icon name={Icon.Name.Trash} fill={Colors.White} />
                <Text>Note deleted</Text>
              </FlexRow>
            </Box>
            <Box sx={undoBtnContainer}>
              <Button
                size="normal"
                variant="unstyled"
                onClick={() => undo(deletedNote, onClose)}
              >
                <Text.BodyBold>Undo</Text.BodyBold>
              </Button>
            </Box>
          </FlexRow>
        ),
      });
    },
    [targetId, onNoteChanged, setCurrentNote, toast, undo, trackNoteAction],
  );

  return (
    <FlexRow key={`editable-note-${targetId}-${updated_at}`}>
      <FlexColumn sx={parentCol}>
        <FlexRow sx={nameAndTimeRow}>
          <Text.SmallBold align="left" color={Colors.Black900}>
            {userName}
          </Text.SmallBold>
          <Text.Small align="right" color={Colors.TextQuiet}>
            {formatNotesDate(updated_at)}
          </Text.Small>
        </FlexRow>
        <Content
          note={currentNote}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          setShowButtons={setShowButtons}
          onNoteChange={setCurrentNote}
          onNoteFocus={() => setShowButtons(true)}
          onNoteBlur={() => {
            if (!currentNote) setShowButtons(false);
          }}
          isOverLimit={isOverLimit}
          setIsOverLimit={setIsOverLimit}
          textAreaRef={textareaRef}
        />
        <FlexRow>
          <Actions
            showButtons={showButtons}
            note={note}
            onSave={handleSave}
            onCancel={handleCancel}
            onEdit={handleEdit}
            onDelete={handleDelete}
            isLoading={createProjectNote.isLoading || updateProjectNote.isLoading}
            readOnly={false}
          />
        </FlexRow>
      </FlexColumn>
    </FlexRow>
  );
};

export default EditableNote;
